import { makeAutoObservable, runInAction } from "mobx";
import vAgent from "../api/vAgent";
import { Project, ProjectAndSubject, Status } from "../models/projectAndSubject";
import { ProjectPurchase } from "../models/projectPurchase";

const initialValues: {
  isLoading: boolean;
  isLoadingProjectPurchase: boolean;
  projectsAndSubjects?: ProjectAndSubject[];
  projectAndSubjectId?: number;
  projectPurchaseList: ProjectPurchase[];
  expandedRowIds: (number | string)[];
  isLoadingProjectStatus: boolean;
  allProjectsAndSubjects: boolean;
  isLoadingProjectsAndSubjects: boolean;
} = {
  isLoading: false,
  isLoadingProjectPurchase: false,
  projectPurchaseList: [],
  expandedRowIds: [],
  isLoadingProjectStatus: false,
  allProjectsAndSubjects: false,
  isLoadingProjectsAndSubjects: false
};

export default class ProjectPurchaseStore {
  isLoading = initialValues.isLoading;
  isLoadingProjectPurchase = initialValues.isLoadingProjectPurchase;
  projectsAndSubjects = initialValues.projectsAndSubjects;
  projectAndSubjectId = initialValues.projectAndSubjectId;
  projectPurchaseList = initialValues.projectPurchaseList;
  expandedRowIds = initialValues.expandedRowIds;
  isLoadingProjectStatus = initialValues.isLoadingProjectStatus;
  allProjectsAndSubjects = initialValues.allProjectsAndSubjects;
  isLoadingProjectsAndSubjects = initialValues.isLoadingProjectsAndSubjects;

  constructor() {
    makeAutoObservable(this);
  }

  resetStore = () => {
    this.isLoading = initialValues.isLoading;
    this.isLoadingProjectPurchase = initialValues.isLoadingProjectPurchase;
    this.projectsAndSubjects = initialValues.projectsAndSubjects;
    this.projectAndSubjectId = initialValues.projectAndSubjectId;
    this.projectPurchaseList = initialValues.projectPurchaseList;
    this.expandedRowIds = initialValues.expandedRowIds;
    this.isLoadingProjectStatus = initialValues.isLoadingProjectStatus;
    this.allProjectsAndSubjects = initialValues.allProjectsAndSubjects;
    this.isLoadingProjectsAndSubjects = initialValues.isLoadingProjectsAndSubjects;
  };

  private setIsLoading = (state: boolean) => {
    runInAction(() => (this.isLoading = state));
  };

  private setIsLoadingProjectPurchase = (state: boolean) => {
    runInAction(() => (this.isLoadingProjectPurchase = state));
  };

  private setIsLoadingProjectStatus = (state: boolean) => {
    runInAction(() => (this.isLoadingProjectStatus = state));
  };

  loadProjectsAndSubjects = async () => {
    let temp: ProjectAndSubject[] | undefined = undefined; //default
    try {
      this.setIsLoading(true);
      temp = await vAgent.ProjectPurchase.getProjectsAndSubjects(this.allProjectsAndSubjects);
    } catch (error) {
      console.log(error);
    } finally {
      this.setIsLoading(false);
    }
    runInAction(() => (this.projectsAndSubjects = temp ? temp : []));
  };

  setAllProjectsAndSubjects = async(value: boolean) => {
    try {
      runInAction(() => {
        this.isLoadingProjectsAndSubjects = true
      })
      const list = await vAgent.Work.getProjectsAndSubjects(value);
      runInAction(() => {
        this.projectsAndSubjects = list ? list : []
        this.allProjectsAndSubjects = value
      })
    } catch (error) {
      console.log(error);
    } finally
    {
      runInAction(() => {
        this.isLoadingProjectsAndSubjects = false
      })
    }
  }

  loadProjectsPurchase = async (projectAndSubjectId: number) => {
    try {
      this.setIsLoadingProjectPurchase(true);
      const temp = await vAgent.ProjectPurchase.loadProjectPurchase(
        projectAndSubjectId
      );
      runInAction(() => {
        this.projectPurchaseList = temp ? temp : [];
        this.projectAndSubjectId = projectAndSubjectId;
        this.expandedRowIds = [];
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.setIsLoadingProjectPurchase(false);
    }
  };

  deleteProjectPurchase = async (id: number) => {
    try {
      this.setIsLoadingProjectPurchase(true);
      const result = await vAgent.ProjectPurchase.delete(id);
      if (result && result > 0) {
        runInAction(() => {
          this.projectPurchaseList = this.projectPurchaseList.filter(
            (x) => x.id !== id
          );
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.setIsLoadingProjectPurchase(false);
      });
    }
  };

  addProjectPurchase = (projectPurchase: ProjectPurchase) => {
    const temp = Array.from(this.projectPurchaseList);
    temp.push(projectPurchase);
    runInAction(() => (this.projectPurchaseList = temp));
  };

  editProjectPurchase = (projectPurchase: ProjectPurchase) => {
    const temp = Array.from(this.projectPurchaseList);
    temp[temp.findIndex((a) => a.id === projectPurchase.id)] = projectPurchase;
    runInAction(() => (this.projectPurchaseList = temp));
  };

  setExpandedRowIds = (ids: (string | number)[]) => {
    runInAction(() => (this.expandedRowIds = ids));
  };

    
  addProject = async (project: Project) => {
    try {
      if (project) {
        const temp = await vAgent.ProjectPurchase.getProjectAndSubjects(
          project.id
        );
        if (temp && temp.length > 0) {
          const newProjectsAndSubjects = this.projectsAndSubjects ? Array.from(this.projectsAndSubjects) : [];
            newProjectsAndSubjects.splice(0, 0, ...temp);

            runInAction(() => {
              this.projectsAndSubjects = newProjectsAndSubjects;
            });
          return newProjectsAndSubjects ? newProjectsAndSubjects[0].id : undefined;
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  setProjectStatus = async (id: number, status: Status) => {
    try {
      this.setIsLoadingProjectStatus(true);
      const projectAndSubject = await vAgent.ProjectPurchase.setProjectStatus(id, status);
      if (projectAndSubject !== undefined) {
        const temp = Array.from(this.projectsAndSubjects!);
        temp[temp.findIndex((x) => x.id === projectAndSubject.id)] = projectAndSubject;
        runInAction(() => (this.projectsAndSubjects = temp));
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.setIsLoadingProjectStatus(false);
    }
  };
}
